import {
  Box, Button, Card, Flex, Grid, Icon, Text,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useToast } from '@chakra-ui/react'
import MasterTable, { Column, RowData } from '../tables/base/MasterTable'
import LoadingTable from '../tables/base/LoadingTable'
import Pagination from '../tables/base/Pagination'
import SearchInput from '../tables/base/SearchInput'
import FilterButton from '../tables/base/FilterButton'
import AvatarCell from '../tables/base/AvatarCell'
import ModalSelect from '../forms/modalForms/modalSelect'
import handleToast from '../HandleToast'
import { HomeIcon } from '../../assets/icons'
import { getOrganizationListAll, switchOrganization } from '../../api/settings/organization'
import { getPlans, updateOrgSubscription } from '../../api/billing'
import { useCurrentSelf } from '../../hooks/auth/useCurrentSelf'

const OrganizationsSettings: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const [selectedOrg, setSelectedOrg] = useState<RowData | null>(null)
  const [selectedPlan, setSelectedPlan] = useState('')
  const itemsPerPage = 15

  const { data: currentSelf } = useCurrentSelf()
  const currentOrgId = currentSelf?.authUser?.organization_id

  const organizations = useQuery({
    queryKey: ['organizations-list-all', page, searchTerm, filters],
    staleTime: 30 * 1000,
    queryFn: () =>
      getOrganizationListAll({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        ...filters,
      }),
  })

  const { data: plans } = useQuery({
    queryKey: ['billing-plans'],
    queryFn: getPlans,
    staleTime: 60 * 1000,
  })

  const homeOrgId = organizations.data?.home_organization_id

  const switchMutation = useMutation({
    mutationFn: (orgId: string) => switchOrganization(orgId),
    onSuccess: () => {
      handleToast(toast, true, t('common.states.success'), t('settings.organizations.switch'))
      window.location.href = '/user/home'
    },
    onError: (error: any) => {
      handleToast(toast, false, t('common.states.error'), error.message)
    },
  })

  const assignPlanMutation = useMutation({
    mutationFn: ({ orgId, plan }: { orgId: string; plan: string }) =>
      updateOrgSubscription(orgId, { plan }),
    onSuccess: () => {
      handleToast(toast, true, t('common.states.success'), t('settings.organizations.planUpdated', 'Plan updated'))
      queryClient.invalidateQueries({ queryKey: ['organizations-list-all'] })
      setSelectedOrg(null)
    },
    onError: (error: any) => {
      handleToast(toast, false, t('common.states.error'), error.message)
    },
  })

  const planOptions = [
    { value: 'free', label: t('settings.billing.plans.free', 'Free (No subscription)') },
    ...(plans ?? []).map((p) => ({
      value: p.key,
      label: `${p.name} (€${p.price_per_seat}/seat)`,
    })),
  ]

  const PLAN_OPTIONS = [
    { id: 'free', name: 'Free' },
    ...(plans ?? []).map((p) => ({ id: p.key, name: p.name })),
  ]

  const STATUS_OPTIONS = [
    { id: 'active', name: 'Active' },
    { id: 'past_due', name: 'Past Due' },
    { id: 'canceled', name: 'Canceled' },
    { id: 'incomplete', name: 'Incomplete' },
    { id: 'no_subscription', name: 'No Subscription' },
  ]

  const getPlanBadgeColors = (plan: string | null) => {
    switch (plan?.toLowerCase()) {
      case 'basic':
        return { bg: 'aqua.0', text: 'aqua.100' }
      case 'pro':
        return { bg: 'primary.0', text: 'primary.100' }
      case 'enterprise':
        return { bg: 'warning.0', text: 'warning.100' }
      default:
        return { bg: 'solid.25', text: 'solid.400' }
    }
  }

  const getStatusBadgeColors = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { bg: 'success.0', text: 'success.100' }
      case 'past_due':
        return { bg: 'warning.0', text: 'warning.100' }
      case 'canceled':
        return { bg: 'error.0', text: 'error.100' }
      case 'incomplete':
        return { bg: 'solid.50', text: 'solid.400' }
      default:
        return { bg: 'solid.25', text: 'solid.300' }
    }
  }

  const columns: Column[] = [
    {
      label: t('settings.organizations.columns.organization'),
      accessor: 'name',
      type: 'string',
      width: '22%',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.logo} name={row.display_name || row.legal_name} showInitials />
      ),
    },
    {
      label: t('settings.organizations.columns.plan'),
      accessor: 'subscription_plan',
      type: 'custom',
      width: '13%',
      Cell: ({ row }: { row: RowData }) => {
        const colors = getPlanBadgeColors(row.subscription_plan)
        return (
          <Box
            display="inline-flex"
            px="10px"
            py="3px"
            borderRadius="full"
            bg={colors.bg}
            border="1px solid"
            borderColor={colors.text}
            alignItems="center"
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
            onClick={(e) => {
              e.stopPropagation()
              setSelectedOrg(row)
              setSelectedPlan(row.subscription_plan || 'free')
            }}
          >
            <Text textStyle="b-xs" fontWeight="500" color={colors.text}>
              {row.subscription_plan || t('settings.organizations.noPlan')}
            </Text>
          </Box>
        )
      },
    },
    {
      label: t('settings.organizations.columns.status'),
      accessor: 'subscription_status',
      type: 'string',
      width: '12%',
      Cell: ({ row }: { row: RowData }) => {
        const colors = getStatusBadgeColors(row.subscription_status)
        return (
          <Box
            display="inline-flex"
            px="10px"
            py="3px"
            borderRadius="full"
            bg={colors.bg}
            border="1px solid"
            borderColor={colors.text}
            alignItems="center"
          >
            <Text textStyle="b-xs" fontWeight="500" color={colors.text}>
              {row.subscription_status || t('settings.organizations.noStatus')}
            </Text>
          </Box>
        )
      },
    },
    {
      label: t('settings.organizations.columns.seats', 'Seats'),
      accessor: 'seats',
      type: 'string',
      width: '8%',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" color="solid.700">
          {row.seats ?? '—'}
        </Text>
      ),
    },
    {
      label: t('settings.organizations.columns.employees'),
      accessor: 'employee_count',
      type: 'string',
      width: '8%',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" color="solid.700">
          {row.employee_count}
        </Text>
      ),
    },
    {
      label: t('settings.organizations.columns.created'),
      accessor: 'created_at',
      type: 'string',
      width: '13%',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="b-xs" color="solid.400" isTruncated>
          {row.created_at
            ? new Date(row.created_at).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : '\u2014'}
        </Text>
      ),
    },
    {
      label: t('settings.organizations.columns.actions'),
      accessor: 'actions',
      type: 'string',
      width: '10%',
      Cell: ({ row }: { row: RowData }) => {
        const isCurrent = row.id === currentOrgId
        const isHome = row.id === homeOrgId
        if (isCurrent) {
          return (
            <Box display="inline-flex" px="10px" py="3px" borderRadius="full" bg="primary.0" border="1px solid" borderColor="primary.25">
              <Text textStyle="b-xs" fontWeight="500" color="primary.100">
                {t('settings.organizations.current')}
              </Text>
            </Box>
          )
        }
        if (isHome) {
          return (
            <Button
              variant="outline"
              size="xs"
              borderRadius="full"
              borderColor="primary.25"
              color="primary.100"
              bg="primary.0"
              fontWeight="500"
              _hover={{ bg: 'primary.25' }}
              onClick={(e) => {
                e.stopPropagation()
                switchMutation.mutate(row.id)
              }}
              isLoading={switchMutation.isPending}
            >
              {t('settings.organizations.home')}
            </Button>
          )
        }
        return (
          <Button
            variant="outline"
            size="xs"
            borderRadius="full"
            borderColor="solid.100"
            color="solid.700"
            fontWeight="500"
            _hover={{ bg: 'solid.25' }}
            onClick={(e) => {
              e.stopPropagation()
              switchMutation.mutate(row.id)
            }}
            isLoading={switchMutation.isPending}
          >
            {t('settings.organizations.switch')}
          </Button>
        )
      },
    },
  ]

  const filteredData = organizations.data?.data || []
  const totalCount = organizations.data?.count || 0
  const maxPage = Math.ceil(totalCount / itemsPerPage)

  const handleSearch = (term: string) => {
    setPage(1)
    setSearchTerm(term)
  }

  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const isOnForeignOrg = homeOrgId && currentOrgId && homeOrgId !== currentOrgId

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput onSearch={handleSearch} />
            <Flex gap="8px">
              <FilterButton
                label={t('settings.organizations.filters.plan')}
                options={PLAN_OPTIONS}
                onApply={(v) => updateFilter('subscription_plan', v)}
              />
              <FilterButton
                label={t('settings.organizations.filters.status')}
                options={STATUS_OPTIONS}
                onApply={(v) => updateFilter('subscription_status', v)}
              />
              {isOnForeignOrg && (
                <Button
                  variant="mono"
                  size="sm"
                  h="32px"
                  px="14px"
                  gap="8px"
                  onClick={() => switchMutation.mutate(homeOrgId)}
                  isLoading={switchMutation.isPending}
                >
                  <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="white"><HomeIcon /></Icon>
                  {t('settings.organizations.switchToHome', 'Switch to Home')}
                </Button>
              )}
            </Flex>
          </Flex>
          {organizations.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box minHeight="58vh">
              <MasterTable
                columns={columns}
                data={filteredData}
                selectedRows={[]}
                onSelectRow={() => {}}
                onSelectAll={() => {}}
                allSelected={false}
                isIndeterminate={false}
                tableType="people"
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={maxPage}
            totalItems={totalCount}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(p: number) => setPage(p)}
          />
        </Box>
      </Grid>

      {/* Plan assignment modal */}
      <Modal isOpen={!!selectedOrg} onClose={() => setSelectedOrg(null)} isCentered size="sm">
        <ModalOverlay />
        <ModalContent borderRadius="12px">
          <ModalHeader fontSize="16px" fontWeight="600">
            {t('settings.organizations.assignPlan', 'Assign Plan')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="16px">
            <Text textStyle="l-xs" color="solid.500" mb="12px">
              {selectedOrg?.display_name || selectedOrg?.legal_name}
              {selectedOrg?.employee_count != null && (
                <Text as="span" color="solid.400"> — {selectedOrg.employee_count} {t('settings.organizations.columns.employees').toLowerCase()}</Text>
              )}
            </Text>
            <ModalSelect
              label={t('settings.organizations.columns.plan')}
              name="plan"
              value={selectedPlan}
              options={planOptions}
              onChange={(e) => setSelectedPlan(e.target.value)}
              hideClear
            />
          </ModalBody>
          <ModalFooter gap="8px">
            <Button variant="ghost" size="sm" onClick={() => setSelectedOrg(null)}>
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="mono"
              size="sm"
              onClick={() => {
                if (selectedOrg) {
                  assignPlanMutation.mutate({ orgId: selectedOrg.id, plan: selectedPlan })
                }
              }}
              isLoading={assignPlanMutation.isPending}
            >
              {t('common.actions.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  )
}

export default OrganizationsSettings
